// kfe-haai-exercise.js — How Awesome Am I exercise screen.
//
// PORT TARGET: Assets/KFE/Scripts/Runtime/Core/UI/Exercise/HAAI/HAAIExerciseScreen.cs
//
// Modalities A–G. Per-question modality drives stimulus + response rendering;
// the engine (HAAIGameLogic) is identical across modalities and only knows
// MMUs. Each modality runs a separate engine session sequentially, with
// firstTry/secondTry/thirdTry/failed counters accumulated across all
// modalities into one final summary.
//
// Modality data flow (Brief 079):
//   • The per-item modality letter(s) come from the catalog `sub` field
//     (master_folder_items.subtype): single (`"H"`) or multi (`"DEFGH"`).
//     `modalitiesFromSub` parses it; empty/absent → ["A"] default.
//   • For synthetic testing, the URL parameter `?modalities=ABCDEFGH` overrides
//     the catalog value and selects the listed modalities (case-insensitive, A–H).
//   • Per-item `modality_definitions` overrides — if Bryan's CMS later
//     supplies them — fall through to MODALITY_DEFINITIONS via resolveModalityDef.
//
// Audio rules (mirrors the Unity port's HAAI bug-#2 fix):
//   • Auto-play target on each new question whose stimulus is audio (A/C/F).
//   • `_lastAudioTarget` guard — never re-play the same target on screen
//     re-renders or feedback transitions; only on a real questionChanged.
//   • Replay button re-plays the current target on demand (audio-stimulus
//     modalities only).
//   • Audio-response modalities (B/D): tap a response tile to preview its
//     audio, then tap "Choose this answer" to commit.
//
// The screen consumes HAAIGameLogic via state snapshots only; the engine
// drives the UI by emitting `questionChanged` / `correct` / `wrong` /
// `exhausted` / `sessionComplete` events.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { HAAIGameLogic, filterMmusForModality } from "../engines/haai-game-logic.js";
import { resolveImage } from "../data/media-paths.js";
import { resolveModalityDef, modalitiesFromSub, VALID_MODALITIES } from "../data/haai-modalities.js";

// Feedback dwell before auto-advancing to the next question. iOS canon is
// `playTimeInterval = 2.5` (KFEHAAIBaseViewController.m:32) — the pause that
// lets the learner register the correct/wrong feedback (and, for Type H, see
// the revealed target image) before the next card. Brief 177 Cluster 5 raised
// this from 1500 to match iOS exactly.
const FEEDBACK_MS = 2500;
// iOS audio auto-cycle gap for Type B/D choice playback: `dispatch_after(1.0s)`
// between each choice (KFEHAAIBViewController.m:109/:125, KFEHAAIDViewController
// .m:135). Brief 177 Cluster 1. Held as an instance field too so tests can zero
// it; this is the production default.
const CHOICE_CYCLE_GAP_MS = 1000;
const DEFAULT_SESSION_SIZE = 4;          // single-modality items (real content)
const PER_MODALITY_SIZE   = 1;           // multi-modality items (synthetic tests)

// Right/wrong answer SFX — Brief 177 Cluster 2. iOS plays a short notify sound
// on EVERY answer tap. Source verification found TWO pairs and we mirror both:
//   • Type A uses `playRightOrWrongAswerAudioWithAnswer:` → rightanswer.m4a /
//     wrongAnswer.wav  (KFEHAAIViewModel.m:534-538).
//   • Types B–J use `playRightOrWrongNotifyAudio:` → mini-positive-1.aac /
//     neutral-1.aac    (KFEHAAIViewModel.m:542-548; called from every Type B–J
//     controller's answer handler).
// CORRECTION to the audit + Chris's ratification (2026-06-21): iOS gates both
// behind a default-OFF "SwitchAudibleYesNo" user setting; the web build plays
// UNCONDITIONALLY (no setting) per Chris's decision — see the Brief 177 handover.
// Assets live in the gitignored-except-here content/Media/sfx/ (see .gitignore).
const SFX_FILES = {
  // Type A pair
  default:  { correct: "rightanswer.m4a", incorrect: "wrongAnswer.wav" },
  // Types B–J pair (the "notify" sounds)
  notify:   { correct: "mini-positive-1.aac", incorrect: "neutral-1.aac" },
};

// Brief 177b Cluster 2 — ONE SFX <audio> shared across every HAAI exercise
// mount (not one per component instance), so repeated mounts don't churn audio
// elements / re-decode. Created lazily on first answer (never on mount, so it
// can't contend with the question's image/audio loads). `preload="none"` — the
// 4 SFX files are tiny and only fetched when first played.
/** @type {HTMLAudioElement | null} */
let _sharedSfxAudio = null;
function sharedSfxAudio() {
  if (_sharedSfxAudio) return _sharedSfxAudio;
  try {
    _sharedSfxAudio = new Audio();
    _sharedSfxAudio.preload = "none";
  } catch {
    _sharedSfxAudio = null;   // Audio() unavailable (some test envs)
  }
  return _sharedSfxAudio;
}

// Per-Type review-heading labels — Brief 177 Cluster 4. iOS sets these per
// modality VC via `KFEHaaiSummaryVC initWithDataDic:reviewTitle:` (verified
// against every TypeX/KFEHAAIXViewController.m showSummaryView):
//   A,B,G → "Review Image-Sound Pairs"   (TypeA:264 / TypeB:302 / TypeG:248)
//   C,D,F,H,J → "Review Sound-Spelling Pairs" (TypeC:298 / TypeD:307 / TypeF:301 / TypeH:443 / TypeJ:442)
//   E → "Review Translation Pairs"        (TypeE:266)
// NOTE — this corrects the Brief 177 audit, which claimed "E/F → Translation"
// and an "F two-label ambiguity". iOS source is unambiguous: F is Sound-Spelling
// and E is the ONLY Translation modality. The value is an i18n KEY.
const REVIEW_LABEL_KEY = {
  A: "completion.haai.reviewPairs",
  B: "completion.haai.reviewPairs",
  G: "completion.haai.reviewPairs",
  C: "completion.haai.reviewSoundSpelling",
  D: "completion.haai.reviewSoundSpelling",
  F: "completion.haai.reviewSoundSpelling",
  H: "completion.haai.reviewSoundSpelling",
  E: "completion.haai.reviewTranslation",
};

// MODE_LABELS — mirrors the iOS canonical labels from the modality table
// comment in KFEHAAIViewModel.m:64-75. `,` separators replaced with ` · ` to
// match the existing web typographic convention. NOT i18n-translated (matches
// the resolver's deterministic language mapping — both are KFE-pedagogy-fixed).
const MODE_LABELS = {
  A: "Hear Sound · Choose Picture",
  B: "See Picture · Recognize Sound",
  C: "Hear Sound · Recognize Word",
  D: "See Word · Recognize Sound",
  E: "See Word · Match to Chinese",
  F: "Hear Word · Match to Chinese",
  G: "See Word · Choose Picture",
  H: "Hear Sound · Spell Word",
};

export class KFEHAAIExercise extends KFEComponent {
  static props = ["itemId"];
  static styles = `
    :host { display: block; max-width: 540px; margin: 0 auto; padding: 0 1rem; box-sizing: border-box; }
    h2 { margin-top: 0; }
    .progress { font-size: 0.85rem; color: var(--kfe-muted);
                text-align: right; margin-bottom: 0.25rem; }
    .modality-label { font-size: 0.85rem; color: var(--kfe-muted);
                      margin-bottom: 0.5rem; }
    .stimulus { display: flex; align-items: center; gap: 1rem;
                margin: 0.75rem 0; padding: 0.75rem; border-radius: 8px;
                background: var(--kfe-surface); }
    .stimulus.image { padding: 0; background: transparent; justify-content: center; }
    .stimulus.image img { max-width: 100%; max-height: 240px;
                          width: auto; height: auto; object-fit: contain;
                          border-radius: 8px; }
    .stimulus.text { justify-content: center; min-height: 4rem; }
    .stimulus .stimulus-text { font-size: 1.5rem; font-weight: 600; text-align: center; }
    .stimulus .label { flex: 1; font-size: 1rem; color: var(--kfe-muted); }
    button.replay { padding: 0.5rem 1rem; border-radius: 8px;
                    border: 1px solid var(--kfe-border); background: transparent;
                    color: var(--kfe-fg); cursor: pointer; }
    button.primary { padding: 0.5rem 1rem; border-radius: 8px;
                     background: var(--kfe-accent); color: var(--kfe-on-accent);
                     border: 0; cursor: pointer; }
    button.primary[disabled] { opacity: 0.5; cursor: default; }
    .start-prompt { padding: 2rem; text-align: center; }
    .choices { margin-top: 0.75rem; }
    .choices.grid  { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; }
    .choices.stack { display: flex; flex-direction: column; gap: 0.5rem; }
    .tile { position: relative; padding: 0;
            background: var(--kfe-surface); border: 1px solid var(--kfe-border);
            border-radius: 8px; overflow: hidden; cursor: pointer;
            display: flex; align-items: center; justify-content: center; }
    .tile.image-tile { aspect-ratio: 1 / 1; }
    .tile.image-tile img { width: 100%; height: 100%; object-fit: contain; }
    .tile.image-tile.no-image { font-size: 0.9rem; padding: 0.5rem; text-align: center; }
    .tile.text-tile { padding: 0.85rem 1rem; font-size: 1.1rem;
                      min-height: 3rem; text-align: center; }
    .tile.audio-tile { padding: 1rem; min-height: 4rem;
                       font-size: 1.5rem; color: var(--kfe-muted); }
    .tile.audio-tile .audio-tile-inner { display: flex; flex-direction: row;
                                         align-items: center; gap: 0.6rem; }
    .tile.audio-tile .audio-label { font-size: 0.85rem; color: var(--kfe-muted); }
    /* Brief 177 Cluster 1 — the auto-cycling tile currently playing its clip
       (iOS HAAIItemBtnCellStateSelected highlight). */
    .tile.audio-tile.playing { outline: 2px solid var(--kfe-accent); outline-offset: -2px;
                               background: color-mix(in srgb, var(--kfe-accent) 14%, var(--kfe-surface)); }
    .tile[disabled] { cursor: default; opacity: 0.55; }
    .tile.eliminated { visibility: hidden; }
    .tile .feedback {
      position: absolute; inset: 0; display: flex; align-items: center;
      justify-content: center; font-size: 3rem; font-weight: 700;
      pointer-events: none;
    }
    .tile .feedback.correct { color: #6fdc8c; }
    .tile .feedback.wrong   { color: #ff8c8c; }
    .confirm { margin-top: 0.75rem; text-align: center; }
    .spell { margin-top: 0.75rem; display: flex; flex-direction: column;
             gap: 0.6rem; align-items: stretch; }
    .spell .row { display: flex; gap: 0.5rem; }
    .spell input { flex: 1; min-width: 0;
                   padding: 0.65rem 0.75rem; font-size: 1.1rem;
                   background: var(--kfe-surface); color: var(--kfe-fg);
                   border: 1px solid var(--kfe-border); border-radius: 8px;
                   outline: none; }
    .spell input:focus { border-color: var(--kfe-accent); }
    .spell input.wrong { border-color: #ff8c8c; animation: kfe-shake 0.35s; }
    .spell input.correct { border-color: #6fdc8c; }
    .spell input[disabled] { opacity: 0.6; }
    .spell .feedback-line { min-height: 1.4rem; text-align: center;
                            font-size: 0.95rem; color: var(--kfe-muted); }
    .spell .feedback-line.wrong { color: #ff8c8c; }
    .spell .feedback-line.correct { color: #6fdc8c; }
    .spell .feedback-line.exhausted { color: #ff8c8c; }
    .spell .feedback-line .answer { font-weight: 600; color: var(--kfe-fg);
                                    margin-left: 0.25rem; }
    /* Brief 177 Cluster 3 — Type H polish */
    .spell-big-image { display: flex; justify-content: center; align-items: center;
                       margin: 0.25rem 0 0.5rem; }
    .spell-big-image img { max-width: 100%; max-height: 200px; width: auto; height: auto;
                           object-fit: contain; border-radius: 8px; }
    .spell-big-image.correct img { outline: 3px solid #6fdc8c; outline-offset: 2px; }
    .spell-history { display: flex; flex-direction: column; gap: 0.15rem;
                     align-items: center; }
    .spell-history .guess { font-size: 1.1rem; font-weight: 600; }
    .spell-history .guess.wrong   { color: #f24d00; }  /* iOS 0xf24d00 */
    .spell-history .guess.correct { color: #76c609; }  /* iOS 0x76c609 */
    .spell-tries { text-align: center; font-size: 0.85rem; color: var(--kfe-muted); }
    .spell-mini-review { display: flex; flex-direction: column; align-items: center;
                         gap: 0.5rem; padding: 0.5rem 0; }
    .spell-mini-review .mini-review-word { font-size: 1.5rem; font-weight: 700; }
    @keyframes kfe-shake {
      0%, 100% { transform: translateX(0); }
      20% { transform: translateX(-6px); }
      40% { transform: translateX(6px); }
      60% { transform: translateX(-4px); }
      80% { transform: translateX(4px); }
    }
    .complete { padding: 1.5rem; text-align: center; }
    .complete .score { font-size: 2.25rem; font-weight: 700;
                       color: var(--kfe-accent); margin: 0.5rem 0; }
    .complete dl { display: grid; grid-template-columns: auto auto;
                   gap: 0.25rem 1rem; max-width: 18rem; margin: 1rem auto; }
    .complete dt { color: var(--kfe-muted); }
    .complete dd { margin: 0; font-weight: 600; text-align: right; }
    .complete .needs-correction { margin-top: 1rem; text-align: left; }
    .complete .needs-correction h3 { font-size: 1rem; margin: 0 0 0.4rem; }
    .complete .needs-correction ul { margin: 0; padding-left: 1.25rem; }
    .complete .needs-correction li { color: var(--kfe-fg); }
    .stub { padding: 1.5rem; text-align: center; color: var(--kfe-muted); }
    .review { text-align: center; }
    .review-num { font-size: 0.85rem; color: var(--kfe-muted); margin-bottom: 0.5rem; }
    .review-image { display: flex; justify-content: center; align-items: center;
                    min-height: 200px; margin: 0.5rem 0; }
    .review-image img { max-width: 100%; max-height: 280px;
                        width: auto; height: auto; object-fit: contain;
                        border-radius: 8px; }
    .review-info { min-height: 2.5rem; display: flex; align-items: center;
                   justify-content: center; gap: 0.6rem; margin: 0.5rem 0; }
    .review-info-btn { width: 2rem; height: 2rem; border-radius: 50%;
                       border: 1px solid var(--kfe-border); background: transparent;
                       color: var(--kfe-muted); font-size: 1.1rem; cursor: pointer;
                       line-height: 1; padding: 0; }
    .review-info-btn.active { color: var(--kfe-accent); border-color: var(--kfe-accent); }
    .review-gloss { font-size: 1.25rem; color: var(--kfe-fg); }
    /* Brief 177b Cluster 6 — the English word shown in C/D/E/F/G review slides. */
    .review-word { font-size: 1.5rem; font-weight: 600; color: var(--kfe-fg);
                   text-align: center; margin: 0.5rem 0; }
    .review-controls { display: flex; justify-content: center; align-items: center;
                       gap: 1.5rem; margin-top: 0.5rem; }
    .review-ctrl { width: 3rem; height: 3rem; border-radius: 50%;
                   border: 1px solid var(--kfe-border); background: transparent;
                   color: var(--kfe-fg); font-size: 1.1rem; cursor: pointer;
                   display: flex; align-items: center; justify-content: center; }
    .review-ctrl.play { background: var(--kfe-accent); color: var(--kfe-on-accent);
                        border: 0; font-size: 1.3rem; }
    .review-ctrl[disabled] { opacity: 0.4; cursor: default; }
  `;

  /** @type {HAAIGameLogic | null} */ engine = null;

  constructor() {
    super();
    this._started = false;
    /** @type {number | null} */ this._lastAudioTarget = null;
    /** @type {Record<number,string>} */ this._tileImageUrls = {};
    /** @type {string | null} */ this._stimulusImageUrl = null;
    /** @type {{ index: number, kind: "correct"|"wrong" } | null} */ this._lastFeedback = null;
    this._choicesLocked = false;
    /** @type {ReturnType<typeof setTimeout>|null} */ this._advanceTimer = null;
    /** @type {import("../engines/haai-game-logic.js").HAAISummary | null} */ this._summary = null;
    this._noContent = false;
    this._completionPersisted = false;
    /** @type {number | undefined} wall-clock ms this run started (Brief 025) */
    this._startedAtMs = undefined;

    /** @type {string[]} */ this._modalityList = [];
    this._modalityIndex = 0;
    this._isMulti = false;
    /** @type {import("../data/mmu-loader.js").Mmu[]} */ this._pool = [];
    /** @type {import("../data/mmu-loader.js").Mmu[]} */ this._metTargets = [];
    this._metEmpty = false;
    /** @type {object | null} */ this._item = null;
    this._totals = { firstTry: 0, secondTry: 0, thirdTry: 0, failed: 0, totalTested: 0 };
    /** @type {import("../data/mmu-loader.js").Mmu[]} */ this._needsCorrection = [];
    /** @type {Array<[string, EventListener]>} */ this._engineListeners = [];
    /** @type {"wrong"|"correct"|"exhausted"|null} */ this._spellFeedback = null;
    /** @type {string | null} */ this._spellExpected = null;
    this._spellAttempt = 0;          // 0 before any submit, 1..3 after misses
    this._spellFocusOnRender = false;

    // Brief 177 Cluster 1 — Type B/D continuous audio-choice auto-cycle. iOS
    // loops the 4 choice clips with 1.0s gaps, highlighting the playing tile,
    // until the learner taps an answer or exhausts (KFEHAAIBViewController.m /
    // KFEHAAIDViewController.m didPlayAudio recursion). `_cycleGen` invalidates
    // an in-flight cycle when the question changes / a tap interrupts / we
    // disconnect; `_cycleGapMs` is the production gap but is overridable in tests.
    this._cycleGen = 0;
    /** @type {ReturnType<typeof setTimeout>|null} */ this._cycleTimer = null;
    /** @type {number|null} */ this._playingTileIndex = null;
    this._cyclePos = 0;
    this._cycleGapMs = CHOICE_CYCLE_GAP_MS;

    // Brief 177 Cluster 3 — Type H polish state.
    /** @type {string[]} */ this._spellGuesses = [];   // per-attempt typed guesses, in order
    /** @type {string | null} */ this._spellBigImageUrl = null; // revealed target image
    this._spellExhaustedPending = false;  // 3rd-miss: show "Play Next", gate advance
    this._miniReviewOpen = false;         // post-exhaust mini-review overlay
    this._miniReviewInfoVisible = false;  // info toggle inside the mini-review

    // Brief 108: HAAI "Review Image-Sound Pairs" review mode. iOS overlays an
    // unscored slideshow (KFEHaaiReviewVC) over the exercise, stepping through
    // the just-tested target MMUs in test order — image + audio, with the
    // Chinese gloss behind an info toggle and manual prev/play/next. We mirror
    // it in place rather than as a route (matches the iOS child-VC overlay and
    // keeps the tested set in memory). See _onReviewPairs.
    this._reviewMode = false;
    this._reviewDone = false;
    this._reviewIndex = 0;
    this._reviewInfoVisible = false;
    /** @type {import("../data/mmu-loader.js").Mmu[]} */ this._testedTargets = [];
    /** @type {string[]} Brief 177b Cluster 6 — modality per tested target (parallel to _testedTargets). */
    this._testedModalities = [];
    /** @type {Record<number,string>} */ this._reviewImageUrls = {};
  }

  async onConnected() {
    await services.mmus.loadMmuRegistry();
    await services.items.loadContentItems();

    const itemId = Number(/** @type {any} */ (this).itemId);
    const item = Number.isFinite(itemId) ? services.items.getItem(itemId) : undefined;
    this._item = item ?? null;

    // Resolve modality list: URL override > catalog `sub` > default ["A"].
    /** @type {string[]} */
    let modalities = modalitiesFromSub(/** @type {any} */ (item)?.sub);
    try {
      const params = new URLSearchParams(window.location.search);
      const override = params.get("modalities");
      if (override) {
        const list = override.toUpperCase().split("").filter((c) => VALID_MODALITIES.includes(c));
        if (list.length > 0) modalities = list;
      }
    } catch { /* URLSearchParams unavailable in some environments */ }

    // Pool: per-item HAAI MMUs first, fall back to the global HAAI pool.
    /** @type {import("../data/mmu-loader.js").Mmu[]} */
    let pool = [];
    if (Number.isFinite(itemId)) {
      const perItem = services.mmus.getMmusForItem(itemId, /*preferHaai*/ true)
        .filter((m) => m.isHaai);
      if (perItem.length > 0) pool = perItem;
    }
    if (pool.length === 0) pool = services.mmus.getAllHaai();

    // Met-gate the target pool per CMS §8.1 (course-designer ratification
    // 2026-05-29; Brief 080). Decoys can be drawn from the wider pool;
    // targets must be drawn from MMUs whose every constituent word is met.
    // Mirrors the SR Auto pattern (sr-engine.js:83-101) and iOS canon
    // (KFEContainerManager.m:308-322 — `mmu_id NOT IN (mmus with an unmet word)`).
    await services.itemMedia.loadItemMedia();
    const completedItemIds = await services.learner.getCompletedItemIds();
    /** @type {Set<number>} */
    const metWords = new Set();
    for (const id of completedItemIds) {
      for (const w of services.itemMedia.getWordsForItem(id)) metWords.add(w);
    }
    const metTargets = pool.filter((mmu) => {
      const wordIds = services.itemMedia.getWordsForMmu(mmu.mmuId);
      // Wordless MMUs can never be "met" — exclude as targets (they may still
      // serve as decoys, no eligibility predicate on the decoy pool).
      return wordIds.length > 0 && wordIds.every((w) => metWords.has(w));
    });

    // Per-modality usability: a modality is in the test plan iff both pools
    // satisfy its minimum (≥1 target; ≥4 decoys for tile modalities, ≥0 for H).
    const usable = [];
    for (const m of modalities) {
      const filteredTargets = filterMmusForModality(metTargets, /** @type {any} */ (m));
      const filteredDecoys  = filterMmusForModality(pool,       /** @type {any} */ (m));
      const minTargets = 1;
      const minDecoys  = m === "H" ? 0 : 4;
      if (filteredTargets.length >= minTargets && filteredDecoys.length >= minDecoys) {
        usable.push(m);
      } else {
        console.warn(
          "[haai] modality", m, "skipped:",
          filteredTargets.length, "targets,", filteredDecoys.length, "decoys",
        );
      }
    }
    if (usable.length === 0) {
      this._noContent = true;
      this._metEmpty = metTargets.length === 0;
      this._scheduleRender();
      return;
    }
    this._modalityList = usable;
    this._isMulti = usable.length > 1;
    this._pool = pool;
    this._metTargets = metTargets;

    // Don't auto-start: Safari iOS user-gesture rule — the first audio play
    // must happen inside a click handler. Render a Start button.
    this._scheduleRender();
  }

  onDisconnected() {
    if (this._advanceTimer) clearTimeout(this._advanceTimer);
    this._cancelChoiceAutoCycle();   // Brief 177 Cluster 1 — no leaked cycle timer
    this._destroyEngine();
    services.audio.stop();
  }

  // ─── Engine wiring ────────────────────────────────────────

  _startModalitySession() {
    this._destroyEngine();
    const modality = this._modalityList[this._modalityIndex];
    // Brief 080: two pools per CMS §8.1 — targets met-only, decoys met+unmet.
    const filteredTargets = filterMmusForModality(this._metTargets, /** @type {any} */ (modality));
    const filteredDecoys  = filterMmusForModality(this._pool,       /** @type {any} */ (modality));
    const sessionSize = this._isMulti ? PER_MODALITY_SIZE : DEFAULT_SESSION_SIZE;
    // Cap targets by what the learner has actually met — never top up from
    // unmet (CMS §8.1: "When met-pool < N, session shrinks instead").
    const cap = Math.min(sessionSize, filteredTargets.length);
    const targets = shuffleCopy(filteredTargets).slice(0, cap);

    // Brief 108: accumulate the actual tested targets, in test order, across
    // every modality session so the post-test review can walk them (mirrors
    // iOS reading the persisted haaiTestContents `target` rows in order).
    this._testedTargets.push(...targets);
    // Brief 177b Cluster 6: record the modality each target was tested under so
    // the review slideshow can render each slide in its own modality's content
    // format (G → text+image, C/D → text, E/F → text+image+Chinese, A/B → image).
    for (let i = 0; i < targets.length; i++) this._testedModalities.push(modality);

    this.engine = new HAAIGameLogic({ targets, decoyPool: filteredDecoys }, sessionSize);
    this._wireEngineListeners();
    this.engine.begin();
  }

  _wireEngineListeners() {
    if (!this.engine) return;
    const onQuestion = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      this._lastFeedback = null;
      this._choicesLocked = false;
      this._tileImageUrls = {};
      this._stimulusImageUrl = null;
      this._spellFeedback = null;
      this._spellExpected = null;
      this._spellAttempt = 0;
      this._spellFocusOnRender = true;
      // Brief 177 Cluster 1 — kill any prior choice auto-cycle.
      this._cancelChoiceAutoCycle();
      // Brief 177 Cluster 3 — reset Type H per-question polish state.
      this._spellGuesses = [];
      this._spellBigImageUrl = null;
      this._spellExhaustedPending = false;
      this._miniReviewOpen = false;
      this._miniReviewInfoVisible = false;
      this._scheduleRender();
      this._maybeAutoplayStimulus(detail.target);
      this._resolveImagesForQuestion(detail.target, detail.choices);
      // Brief 177 Cluster 1 — Type B/D auto-cycle the audio choices on each card.
      this._startChoiceAutoCycle();
    };
    const onCorrect = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      // Brief 177 Cluster 1 — a committed answer ends the auto-cycle.
      this._cancelChoiceAutoCycle();
      // Brief 177 Cluster 2 — right-answer SFX (every modality).
      this._playFeedbackSfx(/*isCorrect*/ true);
      if (detail?.kind === "spell") {
        this._spellFeedback = "correct";
        // Brief 177 Cluster 3 — reveal the target image on a correct spelling
        // (iOS setViewLayoutWithIsRight:YES shows bigImageBtn).
        this._revealSpellBigImage();
      } else {
        this._lastFeedback = { index: detail.answerIndex, kind: "correct" };
      }
      this._choicesLocked = true;
      this._recordAttemptForLearner(/*correct*/ true);
      this._scheduleRender();
      this._scheduleAdvance();
    };
    const onWrong = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      // Brief 177 Cluster 2 — wrong-answer SFX (every modality, every attempt).
      this._playFeedbackSfx(/*isCorrect*/ false);
      if (detail?.kind === "spell") {
        this._spellFeedback = "wrong";
        this._spellAttempt = detail.attemptNumber ?? this._spellAttempt + 1;
        this._spellFocusOnRender = true;
        // Brief 177 Cluster 3 — reveal the target image on a wrong spelling too
        // (iOS setViewLayoutWithIsRight:NO still shows bigImageBtn).
        this._revealSpellBigImage();
        this._scheduleRender();
        // Brief 177b Cluster 1 — do NOT auto-replay the target word here. iOS H
        // (KFEHAAIHViewController isRightAnswer) plays only the answer SFX on a
        // wrong attempt; the learner re-hears the word via the manual play button.
        // The old auto-replay raced the SFX (Issue 1).
        return;
      }
      this._lastFeedback = { index: detail.tappedIndex, kind: "wrong" };
      this._scheduleRender();
      const def = this._currentDef();
      // Brief 177 Cluster 1 — for audio-response modalities (B/D) iOS keeps the
      // choice auto-cycle running over the remaining (non-eliminated) tiles after
      // a wrong tap. Restart it; the engine has already set the wrongMask so the
      // cycle skips the eliminated tile.
      if (def && def.response.media === "audio") {
        this._startChoiceAutoCycle();
        return;
      }
      // Brief 177b Cluster 1 — do NOT auto-replay the target word on a wrong tap.
      // iOS (KFEHAAIAViewController didSelectImageWithSender:) plays only the
      // answer SFX and leaves the learner to re-hear via the manual replay button;
      // the old replay (a Unity-ism: PlayWrong → PlayStimulusAudio) raced the new
      // SFX (Issue 1). _playFeedbackSfx already paused any in-flight target clip.
    };
    const onExhausted = (/** @type {Event} */ e) => {
      const detail = /** @type {CustomEvent} */ (e).detail;
      // Brief 177 Cluster 1 — the final wrong tap ends the auto-cycle.
      this._cancelChoiceAutoCycle();
      // Brief 177 Cluster 2 — the 3rd-miss is still an answer tap: SFX fires.
      this._playFeedbackSfx(/*isCorrect*/ false);
      this._choicesLocked = true;
      this._recordAttemptForLearner(/*correct*/ false);
      if (detail?.kind === "spell") {
        this._spellFeedback = "exhausted";
        this._spellExpected = detail.expected ?? null;
        // Brief 177 Cluster 3 — Type H does NOT auto-advance on the 3rd miss.
        // iOS shows "Sorry, incorrect!" + a "Play Next" button which launches a
        // single-instance mini-review before advancing (KFEHAAIHViewController.m
        // setRecordNewLayoutWithErrorTimes case 3 → playNext: → review VC).
        this._revealSpellBigImage();
        this._spellExhaustedPending = true;
        this._scheduleRender();
        return;
      }
      this._scheduleRender();
      this._scheduleAdvance();
    };
    const onSessionComplete = (/** @type {Event} */ e) => {
      const summary = /** @type {CustomEvent} */ (e).detail;
      this._totals.firstTry    += summary.firstTry;
      this._totals.secondTry   += summary.secondTry;
      this._totals.thirdTry    += summary.thirdTry;
      this._totals.failed      += summary.failed;
      this._totals.totalTested += summary.totalTested;
      if (Array.isArray(summary.needsCorrection)) {
        this._needsCorrection.push(...summary.needsCorrection);
      }

      this._modalityIndex++;
      if (this._modalityIndex < this._modalityList.length) {
        this._startModalitySession();
      } else {
        const total = this._totals.totalTested;
        const raw = total > 0
          ? (this._totals.firstTry + this._totals.secondTry * 0.66 + this._totals.thirdTry * 0.25) / total * 100
          : 0;
        this._summary = {
          totalTested:      total,
          firstTry:         this._totals.firstTry,
          secondTry:        this._totals.secondTry,
          thirdTry:         this._totals.thirdTry,
          failed:           this._totals.failed,
          recognitionScore: Math.round(raw),
          needsCorrection:  this._needsCorrection.slice(),
        };
        this._choicesLocked = true;
        // Brief 060b: the whole HAAI item is complete once every modality
        // session finishes, so its MMU words enter the SR Auto met-pool.
        this._markCompleted();
        this._scheduleRender();
      }
    };

    this.engine.addEventListener("questionChanged", onQuestion);
    this.engine.addEventListener("correct", onCorrect);
    this.engine.addEventListener("wrong", onWrong);
    this.engine.addEventListener("exhausted", onExhausted);
    this.engine.addEventListener("sessionComplete", onSessionComplete);

    this._engineListeners = [
      ["questionChanged",  onQuestion],
      ["correct",          onCorrect],
      ["wrong",            onWrong],
      ["exhausted",        onExhausted],
      ["sessionComplete",  onSessionComplete],
    ];
  }

  _destroyEngine() {
    if (!this.engine) return;
    for (const [type, fn] of this._engineListeners) {
      this.engine.removeEventListener(type, fn);
    }
    this._engineListeners = [];
    this.engine = null;
  }

  _currentDef() {
    if (!this._modalityList.length) return null;
    const modality = this._modalityList[this._modalityIndex];
    return resolveModalityDef(this._item, modality);
  }

  _currentModality() {
    return this._modalityList[this._modalityIndex] ?? "A";
  }

  /**
   * Brief 177 Cluster 4 / Brief 177b Cluster 5 — the per-Type review-heading
   * i18n key.
   *
   * Single-modality (the canonical case — verified in-browser: pure-E renders
   * "Review Translation Pairs") returns that modality's iOS label.
   *
   * Multi-modality is a WEB aggregation: Brief 079 runs each letter of a
   * multi-letter `sub` as a sequential engine session into ONE summary, whereas
   * iOS `HAAIContainerViewController` presents each modality as a SEPARATE
   * sub-lesson with its own per-Type summary (no aggregation). Brief 177's
   * `length === 1` guard fell straight through to "Image-Sound" for ANY
   * multi-letter item — that was the on-device E/F mislabel Chris saw (the walker
   * surfaces ABCDEG-type items under the E/F filters). We now derive the label
   * from the SET of the run's modality labels: if they all map to one heading
   * (e.g. [C,D] → both Sound-Spelling) use it; only a genuinely mixed set falls
   * back to the aggregate "Image-Sound" slideshow heading. Splitting
   * multi-modality into per-modality summaries (true iOS parity) is future work.
   * @returns {string}
   */
  _reviewLabelKey() {
    const keys = new Set(
      (this._modalityList ?? []).map((m) => REVIEW_LABEL_KEY[m] ?? "completion.haai.reviewPairs"),
    );
    if (keys.size === 1) return [...keys][0];
    return "completion.haai.reviewPairs";
  }

  /** True iff this is a single-modality Type-H (spelling) exercise. */
  _isSpellOnly() {
    return this._modalityList.length === 1 && this._modalityList[0] === "H";
  }

  // ─── Action handlers ──────────────────────────────────────

  _handleStart() {
    if (this._started) return;
    this._started = true;
    this._modalityIndex = 0;
    this._totals = { firstTry: 0, secondTry: 0, thirdTry: 0, failed: 0, totalTested: 0 };
    this._needsCorrection = [];
    this._testedTargets = [];
    this._testedModalities = [];
    this._startModalitySession();
  }

  // Brief 046 completion-screen handlers. HAAI's pool comes either from the
  // per-item MMU list or from the global HAAI pool; we only clear streaks
  // for MMUs tied to THIS item (best-effort — clearing global streaks would
  // affect L2B Testing pools and is the wrong unit of work).
  async _onRedo() {
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (Number.isFinite(itemId)) {
      const perItem = services.mmus.getMmusForItem(itemId, /*preferHaai*/ true);
      const ids = perItem.map((m) => m.mmuId);
      try {
        await services.learner.clearItemProgress(itemId, ids);
      } catch (err) {
        console.warn("[haai] clearItemProgress failed", err);
      }
    }
    if (this._advanceTimer) { clearTimeout(this._advanceTimer); this._advanceTimer = null; }
    this._cancelChoiceAutoCycle();
    this._destroyEngine();
    services.audio.stop();
    this._spellGuesses = [];
    this._spellBigImageUrl = null;
    this._spellExhaustedPending = false;
    this._miniReviewOpen = false;
    this._started = false;
    this._summary = null;
    this._needsCorrection = [];
    this._totals = { firstTry: 0, secondTry: 0, thirdTry: 0, failed: 0, totalTested: 0 };
    this._modalityIndex = 0;
    this._choicesLocked = false;
    this._lastFeedback = null;
    this._lastAudioTarget = null;
    this._completionPersisted = false;
    this._exitReview();
    this._testedTargets = [];
    this._testedModalities = [];
    this._scheduleRender();
    this.onConnected();
  }

  _onReturnToLesson() {
    history.back();
  }

  /** Persist item completion once per view (Brief 060b). */
  _markCompleted() {
    if (this._completionPersisted) return;
    const itemId = Number(/** @type {any} */ (this).itemId);
    if (!Number.isFinite(itemId)) return;
    this._completionPersisted = true;
    // Brief 025: enrich the CRM `learner_lesson_items` row with what only this
    // screen knows — the Recognition Index as `score`, and the modality set
    // this run actually tested. `learner-store.markItemCompleted` carries them
    // straight through to the queued row; local completion semantics are
    // untouched.
    services.learner
      .markItemCompleted(itemId, {
        score: Math.round(this._summary?.recognitionScore ?? 0),
        modality: this._testedModalities?.join(",") || undefined,
        startedAt: this._startedAtMs,
      })
      .catch((err) => console.warn("[haai] markItemCompleted failed", err));
    // Brief 025: one `learner_test_instances` row per completed HAAI test
    // (INSERT-only — each run is its own attempt, never revised).
    this._recordTestInstance(itemId);
    // Brief 095: contribute this HAAI test's Recognition Index to the lesson's
    // history series (one {instance, value} per completed HAAI test). The value
    // is the aggregate recognitionScore the summary already holds — equal to the
    // engine snapshot's recognitionIndex. Keyed by the active lesson; the write
    // side lands here (the natural completion hook) per Brief 095 §3.
    this._recordHaaiHistory();
  }

  /**
   * Queue this HAAI run as a CRM test instance (Brief 025). Goes through
   * `recordSession`, the existing analytics funnel, so the row shape lives in
   * learner-store with every other exercise type's rather than here.
   * @param {number} itemId
   */
  _recordTestInstance(itemId) {
    const summary = this._summary;
    if (!summary) return;
    const startedAt = this._startedAtMs ?? Date.now();
    services.learner.recordSession({
      startedAt,
      endedAt: Date.now(),
      exerciseType: "haai",
      itemId,
      summary: {
        attempts: summary.totalTested,
        recognitionScore: summary.recognitionScore,
        passed: summary.failed === 0,
        modality: this._testedModalities?.join(",") ?? null,
      },
    }).catch((err) => console.warn("[haai] recordSession failed", err));
  }

  /** Append this HAAI test's Recognition Index to the active lesson's series. */
  async _recordHaaiHistory() {
    try {
      const value = this._summary?.recognitionScore;
      if (typeof value !== "number") return;
      const lessonId = /** @type {string|undefined} */ (
        await services.learner.getSetting("active_lesson")
      );
      if (!lessonId) return;
      await services.learner.recordLessonHaaiResult(lessonId, value);
    } catch (err) {
      console.warn("[haai] recordLessonHaaiResult failed", err);
    }
  }

  // Brief 108: iOS's "Review Image-Sound Pairs" (KFEHaaiSummaryVC fires
  // HAAISummaryResponseReview) overlays KFEHaaiReviewVC — an unscored slideshow
  // of the just-tested target MMUs in test order. For the Image-Sound layout
  // (HAAIReviewTypeAB) each item shows the image + audio, with the Chinese
  // gloss behind an info toggle and manual previous/play/next. We mirror it in
  // place. The first play() is safe here because we enter from a button tap.
  _onReviewPairs() {
    if (this._testedTargets.length === 0) return;
    if (this._advanceTimer) { clearTimeout(this._advanceTimer); this._advanceTimer = null; }
    this._reviewMode = true;
    this._reviewDone = false;
    this._reviewIndex = 0;
    this._reviewInfoVisible = false;
    this._scheduleRender();
    this._resolveReviewImage(this._reviewIndex);
    this._playReviewAudio();
  }

  /** Leave review mode and return to the completion summary. */
  _exitReview() {
    this._reviewMode = false;
    this._reviewDone = false;
    this._reviewIndex = 0;
    this._reviewInfoVisible = false;
    this._reviewImageUrls = {};
  }

  _reviewPlayCurrent() {
    this._playReviewAudio();
  }

  _reviewNext() {
    // iOS: advancing past the last item shows the "Review Completed" screen.
    if (this._reviewIndex >= this._testedTargets.length - 1) {
      this._reviewDone = true;
      services.audio.stop();
      this._scheduleRender();
      return;
    }
    this._reviewIndex++;
    this._reviewInfoVisible = false;
    this._scheduleRender();
    this._resolveReviewImage(this._reviewIndex);
    this._playReviewAudio();
  }

  _reviewPrev() {
    if (this._reviewIndex <= 0) return;
    this._reviewIndex--;
    this._reviewInfoVisible = false;
    this._scheduleRender();
    this._resolveReviewImage(this._reviewIndex);
    this._playReviewAudio();
  }

  _reviewToggleInfo() {
    this._reviewInfoVisible = !this._reviewInfoVisible;
    this._scheduleRender();
  }

  /** "Back To Review" on the Review Completed screen — restart from item 1. */
  _onBackToReview() {
    this._reviewDone = false;
    this._reviewIndex = 0;
    this._reviewInfoVisible = false;
    this._scheduleRender();
    this._resolveReviewImage(this._reviewIndex);
    this._playReviewAudio();
  }

  /** Play the current review item's audio (unguarded — review replays freely). */
  _playReviewAudio() {
    const mmu = this._testedTargets[this._reviewIndex];
    if (!mmu) return;
    services.audio.play(mmu).catch((e) => {
      console.warn("[haai] review audio play failed for", mmu.mmuId, e);
    });
  }

  /** @param {number} index */
  async _resolveReviewImage(index) {
    const mmu = this._testedTargets[index];
    if (!mmu) return;
    if (this._reviewImageUrls[mmu.mmuId]) return;
    const url = await resolveImage(mmu);
    if (!this._reviewMode) return;
    if (url) {
      this._reviewImageUrls[mmu.mmuId] = url;
      this._scheduleRender();
    }
  }

  _handleReplay() {
    const def = this._currentDef();
    if (!def || def.stimulus.media !== "audio") return;
    const target = this.engine?.snapshot().target ?? null;
    if (target) this._playMmuAudio(target, /*force*/ true);
  }

  /**
   * Tap a choice tile. For every modality (including the audio-response B/D, per
   * Brief 177 Cluster 1 / Chris's "full iOS" decision) a tile tap IS the answer
   * — there is no preview/confirm step. Cancel the auto-cycle first so the
   * playing clip + highlight stop immediately.
   * @param {number} index
   */
  _handleTap(index) {
    if (!this.engine || this._choicesLocked) return;
    if (this.engine.isChoiceEliminated(index)) return;
    this._cancelChoiceAutoCycle();
    this.engine.tapChoice(index);
  }

  /** @param {string} text */
  _handleSpellSubmit(text) {
    if (!this.engine || this._choicesLocked) return;
    if (!text.trim()) return;
    // Brief 177 Cluster 3 — record the typed guess for the per-attempt history
    // rows (iOS updateUTCRWithF:s:t:; showUserTryRecordWithIsRight).
    this._spellGuesses.push(text.trim());
    this.engine.answerSpelling(text);
  }

  _scheduleAdvance() {
    if (this._advanceTimer) clearTimeout(this._advanceTimer);
    this._advanceTimer = setTimeout(() => {
      this._advanceTimer = null;
      this.engine?.skip();
    }, FEEDBACK_MS);
  }

  // ─── Audio ────────────────────────────────────────────────

  /**
   * Brief 177 Cluster 2 — the SFX filename for an answer outcome, keyed off the
   * current modality (Type A uses the "default" pair, all others the "notify"
   * pair). Pure + exported via the instance for unit assertions.
   * @param {boolean} isCorrect
   * @returns {string}
   */
  _sfxFileFor(isCorrect) {
    const pair = this._currentModality() === "A" ? SFX_FILES.default : SFX_FILES.notify;
    return isCorrect ? pair.correct : pair.incorrect;
  }

  /**
   * Play the right/wrong answer SFX on its own audio element.
   *
   * Brief 177b Cluster 1 — answer-audio sequence (no overlap). iOS plays the
   * answer SFX immediately on the answer tap and nothing else
   * (`KFEHAAIAViewController didSelectImageWithSender:` →
   * `playRightOrWrongAswerAudioWithAnswer:`; no target-word replay), then
   * advances after `playTimeInterval` (2.5s). The web's pre-177 "replay the
   * target on a wrong answer" (a Unity-ism — see the removed branch in onWrong)
   * raced with this SFX. We mirror iOS: silence any in-flight exercise/cycle
   * clip first (so the SFX is clean), then play the SFX. Advance stays on the
   * independent FEEDBACK_MS timer — the short SFX finishes well within the dwell.
   *
   * Brief 177b Cluster 2 — the SFX element is a MODULE-LEVEL singleton
   * (`sharedSfxAudio`) reused across every HAAI mount, not one `<audio>` per
   * component instance, so repeated exercise mounts don't churn audio elements.
   * @param {boolean} isCorrect
   */
  _playFeedbackSfx(isCorrect) {
    try {
      // Silence any still-playing target/cycle clip so the SFX doesn't overlap.
      // pause() (not stop()) — stop() would emit "stopped" and resume the Global
      // Playlist mid-exercise (Brief 091); pause() just halts the element.
      try { services.audio.pause(); } catch { /* non-fatal */ }
      const el = sharedSfxAudio();
      if (!el) return;
      el.src = `/content/Media/sfx/${this._sfxFileFor(isCorrect)}`;
      el.currentTime = 0;
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => { /* gesture/decode races — non-fatal */ });
    } catch { /* Audio() unavailable in some test envs — non-fatal */ }
  }

  /** @param {import("../data/mmu-loader.js").Mmu | null} target */
  _maybeAutoplayStimulus(target) {
    const def = this._currentDef();
    if (!def || def.stimulus.media !== "audio" || !target) return;
    this._playMmuAudio(target);
  }

  /**
   * @param {import("../data/mmu-loader.js").Mmu | null} target
   * @param {boolean} [force=false]
   */
  _playMmuAudio(target, force = false) {
    if (!target) return;
    if (!force && this._lastAudioTarget === target.mmuId) return;
    this._lastAudioTarget = target.mmuId;
    services.audio.play(target).catch((e) => {
      console.warn("[haai] audio play failed for", target.mmuId, e);
    });
  }

  // ─── Type B/D choice auto-cycle (Brief 177 Cluster 1) ─────
  //
  // iOS Type B/D play the 4 audio choices on a 1.0s-gap loop, highlighting the
  // currently-playing tile (changeItemCellState), and a tile tap IS the answer
  // (didSelectItemBtnCellView). The cycle keeps looping over the not-yet-answered
  // choices until the learner taps a correct answer or exhausts the 3 strikes
  // (KFEHAAIBViewController.m didPlayAudio: recursion; KFEHAAIBViewModel.m
  // willPlayTemCurrentContent skips already-answered indices). We mirror that
  // here. CORRECTION to the Brief 177 audit, which said the cycle plays once
  // then stops — iOS loops continuously.

  /**
   * Start (or restart) the audio-choice auto-cycle. No-op for any modality whose
   * response is not audio (i.e. everything but B/D).
   */
  _startChoiceAutoCycle() {
    const def = this._currentDef();
    if (!def || def.response.media !== "audio") return;
    if (!this.engine || this._choicesLocked) return;
    this._cancelChoiceAutoCycle();
    const gen = ++this._cycleGen;
    this._cyclePos = this._nextCycleIndex(0) ?? 0;
    // First clip fires after one gap (iOS Type B waits 1.0s before the first
    // play; we apply it uniformly for B and D).
    this._cycleTimer = setTimeout(() => this._runCycleStep(gen), this._cycleGapMs);
  }

  /** Cancel any in-flight cycle; the gen bump invalidates pending async steps. */
  _cancelChoiceAutoCycle() {
    this._cycleGen++;
    if (this._cycleTimer) { clearTimeout(this._cycleTimer); this._cycleTimer = null; }
    if (this._playingTileIndex !== null) {
      this._playingTileIndex = null;
      this._scheduleRender();
    }
  }

  /**
   * Next non-eliminated choice index at/after `from`, wrapping. Null if every
   * choice is eliminated (mirrors iOS willPlayTemCurrentContent skipping answered).
   * @param {number} from
   * @returns {number | null}
   */
  _nextCycleIndex(from) {
    const n = this.engine?.snapshot().choices.length ?? 0;
    if (n === 0) return null;
    for (let step = 0; step < n; step++) {
      const i = (from + step) % n;
      if (!this.engine?.isChoiceEliminated(i)) return i;
    }
    return null;
  }

  /**
   * One cycle step: highlight + play choice at `_cyclePos`, then schedule the
   * next after a 1.0s gap. Guarded by `gen` so a question change / tap / detach
   * stops it cleanly.
   * @param {number} gen
   */
  async _runCycleStep(gen) {
    this._cycleTimer = null;
    if (gen !== this._cycleGen || !this.engine || this._choicesLocked) return;
    const idx = this._nextCycleIndex(this._cyclePos);
    if (idx == null) return;
    const choices = this.engine.snapshot().choices;
    const mmu = choices[idx];
    this._playingTileIndex = idx;
    this._scheduleRender();
    try {
      await services.audio.play(mmu);   // resolves on `ended` (or supersede)
    } catch { /* a superseded/failed clip must not break the loop */ }
    if (gen !== this._cycleGen || this._choicesLocked) return;
    this._cyclePos = (idx + 1) % choices.length;
    this._cycleTimer = setTimeout(() => this._runCycleStep(gen), this._cycleGapMs);
  }

  // ─── Type H polish (Brief 177 Cluster 3) ──────────────────

  /**
   * Resolve + reveal the current target's image (the Type H "big image" — iOS
   * bigImageBtn). Shown on both correct and wrong attempts.
   */
  async _revealSpellBigImage() {
    const target = this.engine?.snapshot().target ?? null;
    if (!target) return;
    const targetId = target.mmuId;
    const url = await resolveImage(target);
    if (this.engine?.snapshot().target?.mmuId !== targetId) return;  // moved on
    if (url) {
      this._spellBigImageUrl = url;
      this._scheduleRender();
    }
  }

  /** "Play Next" after the 3rd spelling miss → open the single-instance review. */
  _openSpellMiniReview() {
    this._miniReviewOpen = true;
    this._miniReviewInfoVisible = false;
    this._scheduleRender();
    // iOS HSingleInstant auto-plays the target audio on load.
    const target = this.engine?.snapshot().target ?? null;
    if (target) this._playMmuAudio(target, /*force*/ true);
  }

  _miniReviewReplay() {
    const target = this.engine?.snapshot().target ?? null;
    if (target) this._playMmuAudio(target, /*force*/ true);
  }

  _miniReviewToggleInfo() {
    this._miniReviewInfoVisible = !this._miniReviewInfoVisible;
    this._scheduleRender();
  }

  /** Dismiss the mini-review and advance to the next question (iOS playNext → next). */
  _closeSpellMiniReviewAndAdvance() {
    this._miniReviewOpen = false;
    this._spellExhaustedPending = false;
    this._scheduleRender();
    this.engine?.skip();
  }

  // ─── Image resolution ─────────────────────────────────────

  /**
   * Preload images for the current question. Only fetches what the modality
   * actually needs (stimulus image and/or response tile images).
   * @param {import("../data/mmu-loader.js").Mmu | null} target
   * @param {import("../data/mmu-loader.js").Mmu[]} choices
   */
  async _resolveImagesForQuestion(target, choices) {
    const def = this._currentDef();
    if (!def) return;
    const targetId = target?.mmuId ?? null;

    const promises = [];

    if (def.stimulus.media === "image" && target) {
      promises.push(resolveImage(target).then((url) => {
        if (this.engine?.snapshot().target?.mmuId !== targetId) return;
        if (url) this._stimulusImageUrl = url;
      }));
    }

    if (def.response.media === "image") {
      const next = /** @type {Record<number,string>} */ ({});
      promises.push(Promise.all(choices.map(async (m) => {
        const url = await resolveImage(m);
        if (url) next[m.mmuId] = url;
      })).then(() => {
        if (this.engine?.snapshot().target?.mmuId !== targetId) return;
        this._tileImageUrls = next;
      }));
    }

    await Promise.all(promises);
    if (this.engine?.snapshot().target?.mmuId !== targetId) return;
    this._scheduleRender();
  }

  // ─── Persistence ──────────────────────────────────────────

  /** @param {boolean} correct */
  _recordAttemptForLearner(correct) {
    const target = this.engine?.snapshot().target ?? null;
    if (!target) return;
    services.learner.recordMmuAttempt(target.mmuId, correct, 3).catch((err) => {
      console.warn("[haai] recordMmuAttempt failed", err);
    });
  }

  // ─── Render ───────────────────────────────────────────────

  render() {
    if (this._noContent) {
      // Brief 080: distinguish "no eligible MMUs at all" (legacy) from "the
      // learner hasn't met any qualifying words yet". The latter is the
      // expected state on a fresh device — no global-pool fallback by design.
      const msg = this._metEmpty
        ? "No words to test yet — finish some L2B / HAAI lessons first."
        : "No HAAI items available for this exercise.";
      return html`<div class="stub">
        <h2>${t("haai.title")}</h2>
        <p>${msg}</p>
      </div>`;
    }
    if (!this._modalityList.length) return html`<p>${t("loading")}</p>`;

    if (this._reviewMode) return this._renderReview();
    if (this._summary) return this._renderSummary(this._summary);
    if (!this._started) return this._renderStart();
    if (!this.engine) return html`<p>${t("loading")}</p>`;

    const s = this.engine.snapshot();
    if (!s.target) return html`<p>${t("loading")}</p>`;

    return this._renderQuestion(s);
  }

  _renderStart() {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "primary";
    btn.textContent = t("lesson.start");
    btn.addEventListener("click", () => this._handleStart());

    const list = this._modalityList.join(" · ");
    return html`
      <h2>${t("haai.title")}</h2>
      <div class="modality-label">${list}</div>
      <div class="start-prompt">${btn}</div>
    `;
  }

  /** @param {import("../engines/haai-game-logic.js").HAAISnapshot} s */
  _renderQuestion(s) {
    const def = this._currentDef();
    if (!def) return html`<p>${t("loading")}</p>`;

    const modality = this._currentModality();
    const grandTotal = this._isMulti
      ? this._modalityList.length * PER_MODALITY_SIZE
      : DEFAULT_SESSION_SIZE;
    const overallIndex = this._totals.totalTested + s.currentIndex + 1;

    const stimulus = this._renderStimulus(def, s.target);
    const stimulusEl = document.createElement("div");
    stimulusEl.className = "stimulus " + def.stimulus.media;
    stimulusEl.appendChild(stimulus);

    const frag = html`
      <div class="progress">${overallIndex} / ${grandTotal} · ${modality}</div>
      <h2>${t("haai.title")} · ${modality}</h2>
      <div class="modality-label">${MODE_LABELS[modality] || ""}</div>
    `;
    frag.appendChild(stimulusEl);

    if (def.response.media === "spell") {
      frag.appendChild(this._renderSpellResponse(s));
      return frag;
    }

    const responses = s.choices.map((m, i) => this._renderResponse(def, m, i));
    const isAudioResp = def.response.media === "audio";

    const choices = document.createElement("div");
    choices.className = "choices " + (isAudioResp || def.response.media === "text" ? "stack" : "grid");
    for (const r of responses) choices.appendChild(r);
    frag.appendChild(choices);

    // Brief 177 Cluster 1 — no "Choose this answer" confirm step for B/D: a tile
    // tap is the answer (full iOS model). The 4 clips auto-cycle for listening.

    return frag;
  }

  /** @param {import("../engines/haai-game-logic.js").HAAISnapshot} s */
  _renderSpellResponse(s) {
    // Brief 177 Cluster 3 — after the 3rd miss, "Play Next" opens a
    // single-instance mini-review that takes over the response area.
    if (this._miniReviewOpen) return this._renderSpellMiniReview(s);

    const wrap = document.createElement("form");
    wrap.className = "spell";
    wrap.setAttribute("autocomplete", "off");

    // Big target-image reveal (iOS bigImageBtn), shown on correct AND wrong.
    if (this._spellBigImageUrl) {
      const imgWrap = document.createElement("div");
      imgWrap.className = "spell-big-image" + (this._spellFeedback === "correct" ? " correct" : "");
      const img = document.createElement("img");
      img.src = this._spellBigImageUrl;
      img.alt = s.target?.phrase ?? "";
      img.loading = "eager";
      imgWrap.appendChild(img);
      wrap.appendChild(imgWrap);
    }

    // Per-attempt guess history (iOS userTryRecordLabels): each prior guess in
    // orange; the winning attempt (on a correct answer) in green. CORRECTION to
    // the Brief 177 audit, which described per-character red/green diffing — iOS
    // colours the WHOLE guess label (showUserTryRecordWithIsRight:).
    const history = this._renderSpellHistory();
    if (history) wrap.appendChild(history);

    if (this._spellExhaustedPending) {
      // 3rd miss: "Sorry, incorrect!" + "Play Next" (no input; gated advance).
      const sorry = document.createElement("div");
      sorry.className = "feedback-line exhausted";
      sorry.textContent = "Sorry, incorrect!";
      wrap.appendChild(sorry);

      const playNext = document.createElement("button");
      playNext.type = "button";
      playNext.className = "primary";
      playNext.textContent = "Play Next";
      playNext.addEventListener("click", () => this._openSpellMiniReview());
      const pnWrap = document.createElement("div");
      pnWrap.className = "confirm";
      pnWrap.appendChild(playNext);
      wrap.appendChild(pnWrap);
      return wrap;
    }

    const row = document.createElement("div");
    row.className = "row";

    const input = document.createElement("input");
    input.type = "text";
    input.setAttribute("autocomplete", "off");
    input.setAttribute("autocorrect", "off");
    input.setAttribute("autocapitalize", "off");
    input.setAttribute("spellcheck", "false");
    input.setAttribute("inputmode", "text");
    input.setAttribute("enterkeyhint", "done");
    input.placeholder = "Type the word you heard";
    input.disabled = this._choicesLocked;
    if (this._spellFeedback === "wrong") input.classList.add("wrong");
    if (this._spellFeedback === "correct") input.classList.add("correct");
    row.appendChild(input);

    const submitBtn = document.createElement("button");
    submitBtn.type = "submit";
    submitBtn.className = "primary";
    submitBtn.textContent = "Check";
    submitBtn.disabled = this._choicesLocked;
    row.appendChild(submitBtn);

    wrap.appendChild(row);

    // Tries counter (iOS triesLabel) — current attempt number, 1..3.
    if (!this._choicesLocked) {
      const tries = document.createElement("div");
      tries.className = "spell-tries";
      tries.textContent = `Tries: ${Math.min(3, this._spellAttempt + 1)}`;
      wrap.appendChild(tries);
    }

    const feedback = this._renderSpellFeedbackLine(s);
    wrap.appendChild(feedback);

    wrap.addEventListener("submit", (e) => {
      e.preventDefault();
      this._handleSpellSubmit(input.value);
    });

    if (this._spellFocusOnRender && !this._choicesLocked) {
      this._spellFocusOnRender = false;
      queueMicrotask(() => {
        try { input.focus(); } catch { /* ignore */ }
      });
    }

    return wrap;
  }

  /**
   * Brief 177 Cluster 3 — the per-attempt guess history rows. Returns null when
   * there are no guesses yet.
   * @returns {HTMLElement | null}
   */
  _renderSpellHistory() {
    if (!this._spellGuesses.length) return null;
    const correctIdx = this._spellFeedback === "correct" ? this._spellGuesses.length - 1 : -1;
    const box = document.createElement("div");
    box.className = "spell-history";
    this._spellGuesses.forEach((g, i) => {
      const row = document.createElement("div");
      row.className = "guess" + (i === correctIdx ? " correct" : " wrong");
      row.textContent = g;
      box.appendChild(row);
    });
    return box;
  }

  /**
   * Brief 177 Cluster 3 — the single-instance post-exhaust mini-review (iOS
   * KFEHaaiReviewVC HAAIReviewTypeHSingleInstant): target image + English word,
   * an info toggle revealing the Chinese gloss, replayable audio, and a single
   * "Next" button that dismisses and advances. Bounded to this component (no
   * router navigation) per the brief's scope guard.
   * @param {import("../engines/haai-game-logic.js").HAAISnapshot} s
   */
  _renderSpellMiniReview(s) {
    const target = s.target;
    const wrap = document.createElement("div");
    wrap.className = "spell-mini-review";

    const imgWrap = document.createElement("div");
    imgWrap.className = "review-image";
    if (this._spellBigImageUrl) {
      const img = document.createElement("img");
      img.src = this._spellBigImageUrl;
      img.alt = target?.phrase ?? "";
      imgWrap.appendChild(img);
    }
    wrap.appendChild(imgWrap);

    const word = document.createElement("div");
    word.className = "mini-review-word";
    word.lang = "en";
    word.textContent = target?.phrase ?? "";
    wrap.appendChild(word);

    // Info toggle → Chinese gloss (iOS infoBtn → infoLabel = titleZH).
    const info = document.createElement("div");
    info.className = "review-info";
    const infoBtn = document.createElement("button");
    infoBtn.type = "button";
    infoBtn.className = "review-info-btn" + (this._miniReviewInfoVisible ? " active" : "");
    infoBtn.setAttribute("aria-label", t("haai.review.info"));
    infoBtn.setAttribute("aria-pressed", String(this._miniReviewInfoVisible));
    infoBtn.textContent = "ⓘ";
    infoBtn.addEventListener("click", () => this._miniReviewToggleInfo());
    info.appendChild(infoBtn);
    if (this._miniReviewInfoVisible) {
      const gloss = document.createElement("span");
      gloss.className = "review-gloss";
      gloss.lang = "zh-Hans";
      gloss.textContent = target?.phraseZh || target?.phrase || "";
      info.appendChild(gloss);
    }
    wrap.appendChild(info);

    const controls = document.createElement("div");
    controls.className = "review-controls";
    const playBtn = this._reviewControlButton("▶", t("haai.review.play"), () => this._miniReviewReplay());
    playBtn.classList.add("play");
    const nextBtn = this._reviewControlButton("▶❘", t("haai.review.next"), () => this._closeSpellMiniReviewAndAdvance());
    controls.append(playBtn, nextBtn);
    wrap.appendChild(controls);

    return wrap;
  }

  /** @param {import("../engines/haai-game-logic.js").HAAISnapshot} s */
  _renderSpellFeedbackLine(s) {
    const line = document.createElement("div");
    line.className = "feedback-line";
    if (this._spellFeedback === "correct") {
      line.classList.add("correct");
      line.textContent = "✓ Correct";
      return line;
    }
    if (this._spellFeedback === "exhausted") {
      line.classList.add("exhausted");
      const answer = this._spellExpected ?? s.target?.phrase ?? "";
      line.textContent = "The correct spelling is";
      const ans = document.createElement("span");
      ans.className = "answer";
      ans.textContent = answer;
      line.appendChild(ans);
      return line;
    }
    if (this._spellFeedback === "wrong") {
      line.classList.add("wrong");
      // iOS tryAgainLabel = "Try Again!" (KFEHAAIHViewController updateLanguageText
      // 1101). The attempt count is carried by the separate Tries counter.
      line.textContent = "Try Again!";
      return line;
    }
    line.textContent = "Listen carefully and type the word";
    return line;
  }

  /**
   * @param {import("../data/haai-modalities.js").HAAIModalityDef} def
   * @param {import("../data/mmu-loader.js").Mmu | null} target
   */
  _renderStimulus(def, target) {
    switch (def.stimulus.media) {
      case "audio": return this._renderStimulusAudio();
      case "image": return this._renderStimulusImage(target);
      case "text":  return this._renderStimulusText(target, def.stimulus.lang ?? "target");
      default: return document.createElement("span");
    }
  }

  _renderStimulusAudio() {
    const wrap = document.createElement("span");
    wrap.style.display = "contents";
    const label = document.createElement("span");
    label.className = "label";
    label.textContent = t("audio.replay");
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "replay";
    btn.textContent = "▶ " + t("audio.replay");
    btn.addEventListener("click", () => this._handleReplay());
    wrap.appendChild(label);
    wrap.appendChild(btn);
    return wrap;
  }

  /** @param {import("../data/mmu-loader.js").Mmu | null} target */
  _renderStimulusImage(target) {
    const img = document.createElement("img");
    img.alt = target?.phrase ?? "";
    img.loading = "eager";
    if (this._stimulusImageUrl) img.src = this._stimulusImageUrl;
    return img;
  }

  /**
   * @param {import("../data/mmu-loader.js").Mmu | null} target
   * @param {"target"|"source"} lang
   */
  _renderStimulusText(target, lang) {
    const tag = languageTag(lang);
    const text = target ? textForLang(target, lang) : "";
    const span = document.createElement("span");
    span.className = "stimulus-text";
    span.lang = tag;
    span.textContent = text;
    return span;
  }

  /**
   * @param {import("../data/haai-modalities.js").HAAIModalityDef} def
   * @param {import("../data/mmu-loader.js").Mmu} mmu
   * @param {number} index
   */
  _renderResponse(def, mmu, index) {
    switch (def.response.media) {
      case "image": return this._renderImageTile(mmu, index);
      case "audio": return this._renderAudioTile(mmu, index);
      case "text":  return this._renderTextTile(mmu, index, def.response.lang ?? "target");
      default: return document.createElement("button");
    }
  }

  /**
   * @param {import("../data/mmu-loader.js").Mmu} mmu
   * @param {number} index
   */
  _renderImageTile(mmu, index) {
    const eliminated = this.engine?.isChoiceEliminated(index) ?? false;
    const url = this._tileImageUrls[mmu.mmuId];
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tile image-tile" + (url ? "" : " no-image") + (eliminated ? " eliminated" : "");
    if (eliminated || this._choicesLocked) btn.disabled = true;

    if (url) {
      const img = document.createElement("img");
      img.src = url;
      img.alt = mmu.phrase;
      img.loading = "lazy";
      btn.appendChild(img);
    } else {
      const span = document.createElement("span");
      span.textContent = mmu.phrase;
      btn.appendChild(span);
    }
    this._appendFeedback(btn, index);
    btn.addEventListener("click", () => this._handleTap(index));
    return btn;
  }

  /**
   * @param {import("../data/mmu-loader.js").Mmu} mmu
   * @param {number} index
   */
  _renderAudioTile(mmu, index) {
    const eliminated = this.engine?.isChoiceEliminated(index) ?? false;
    // Brief 177 Cluster 1 — highlight the tile whose clip the auto-cycle is
    // currently playing (iOS changeItemCellState → HAAIItemBtnCellStateSelected).
    const isPlaying = this._playingTileIndex === index;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tile audio-tile" + (eliminated ? " eliminated" : "") + (isPlaying ? " playing" : "");
    if (isPlaying) btn.setAttribute("data-playing", "true");
    if (eliminated || this._choicesLocked) btn.disabled = true;
    const inner = document.createElement("div");
    inner.className = "audio-tile-inner";
    const icon = document.createElement("span");
    icon.textContent = isPlaying ? "🔊" : "▶";
    const label = document.createElement("span");
    label.className = "audio-label";
    // iOS audio cells are labelled "Option N" (KFEHAAIBViewController initItemBtnCells).
    label.textContent = `Option ${index + 1}`;
    inner.appendChild(icon);
    inner.appendChild(label);
    btn.appendChild(inner);
    this._appendFeedback(btn, index);
    // A tile tap is the answer (full iOS model — no preview/confirm).
    btn.addEventListener("click", () => this._handleTap(index));
    return btn;
  }

  /**
   * @param {import("../data/mmu-loader.js").Mmu} mmu
   * @param {number} index
   * @param {"target"|"source"} lang
   */
  _renderTextTile(mmu, index, lang) {
    const eliminated = this.engine?.isChoiceEliminated(index) ?? false;
    const tag = languageTag(lang);
    const text = textForLang(mmu, lang);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "tile text-tile" + (eliminated ? " eliminated" : "");
    if (eliminated || this._choicesLocked) btn.disabled = true;
    const span = document.createElement("span");
    span.lang = tag;
    span.textContent = text;
    btn.appendChild(span);
    this._appendFeedback(btn, index);
    btn.addEventListener("click", () => this._handleTap(index));
    return btn;
  }

  /**
   * @param {HTMLElement} btn
   * @param {number} index
   */
  _appendFeedback(btn, index) {
    if (this._lastFeedback && this._lastFeedback.index === index) {
      const fb = document.createElement("div");
      fb.className = "feedback " + this._lastFeedback.kind;
      fb.textContent = this._lastFeedback.kind === "correct" ? "✓" : "✗";
      btn.appendChild(fb);
    }
  }

  /** @param {import("../engines/haai-game-logic.js").HAAISummary} summary */
  _renderSummary(summary) {
    // Build the score + stats block as slot content for the shared
    // completion screen (Brief 046).
    const spellOnly = this._isSpellOnly();
    const block = document.createElement("div");
    block.className = "complete";
    const score = document.createElement("div");
    score.className = "score";
    score.textContent = String(summary.recognitionScore);
    block.appendChild(score);

    // Brief 177 Cluster 3 — Type H uses iOS's distinct summary titles
    // (KFEHAAIHViewController.m showSummaryView titleArr: "Words Needing
    // Correction" + "Spelling Accuracy Score" replace "Failed"/"Total"/
    // "Recognition Score"). Every other modality keeps the shared titles.
    /** @type {Array<[string, number]>} */
    const rows = spellOnly
      ? [
          ["Items Tested",        summary.totalTested],
          ["First Try Correct",   summary.firstTry],
          ["Second Try Correct",  summary.secondTry],
          ["Third Try Correct",   summary.thirdTry],
          ["Words Needing Correction", summary.failed],
          ["Spelling Accuracy Score",  summary.recognitionScore],
        ]
      : [
          ["First try",  summary.firstTry],
          ["Second try", summary.secondTry],
          ["Third try",  summary.thirdTry],
          ["Failed",     summary.failed],
          ["Total",      summary.totalTested],
        ];

    const dl = document.createElement("dl");
    for (const [label, val] of rows) {
      const dt = document.createElement("dt"); dt.textContent = String(label);
      const dd = document.createElement("dd"); dd.textContent = String(val);
      dl.append(dt, dd);
    }
    block.appendChild(dl);

    if (summary.needsCorrection && summary.needsCorrection.length > 0) {
      const nc = document.createElement("div");
      nc.className = "needs-correction";
      const h = document.createElement("h3");
      h.textContent = "Words needing correction";
      nc.appendChild(h);
      const ul = document.createElement("ul");
      const seen = new Set();
      for (const m of summary.needsCorrection) {
        if (seen.has(m.mmuId)) continue;
        seen.add(m.mmuId);
        const li = document.createElement("li");
        li.textContent = m.phrase || "(unknown)";
        ul.appendChild(li);
      }
      nc.appendChild(ul);
      block.appendChild(nc);
    }

    // Brief 049: iOS-canonical HAAI summary (KFEHaaiSummaryVC) stacks full-width
    // buttons vertically — "Redo Test" (重做测试), "Leave Test" (离开测试) and,
    // except for Type H, a per-Type "Review …" button.
    //
    // Brief 177 Cluster 4 — the review-button label is now per-Type (REVIEW_LABEL_KEY
    // via _reviewLabelKey), correcting the prior hard-coded A/G label.
    // Brief 177 Cluster 3 — Type H sets isHaveNoReview=YES (KFEHAAIHViewController
    // .m:445), so a spell-only exercise omits the Review button entirely.
    const screen = document.createElement("kfe-completion-screen");
    /** @type {any} */ (screen).message = t("exercise.complete");
    /** @type {any} */ (screen).layout = "column";
    /** @type {Array<{label: string, eventName: string, primary?: boolean}>} */
    const actions = [];
    if (!spellOnly) {
      actions.push({ label: t(this._reviewLabelKey()), eventName: "kfe:haai:reviewPairs" });
    }
    actions.push({ label: t("completion.haai.redoTest"), eventName: "kfe:haai:redoTest", primary: true });
    actions.push({ label: t("completion.haai.leaveTest"), eventName: "kfe:haai:leaveTest" });
    /** @type {any} */ (screen).actions = actions;
    screen.appendChild(block);
    screen.addEventListener("kfe:haai:reviewPairs", () => this._onReviewPairs());
    screen.addEventListener("kfe:haai:redoTest", () => this._onRedo());
    screen.addEventListener("kfe:haai:leaveTest", () => this._onReturnToLesson());
    return html`${screen}`;
  }

  // ─── Review mode (Brief 108) ──────────────────────────────

  /**
   * Render the unscored review slideshow (or the "Review Completed" screen).
   * Mirrors iOS KFEHaaiReviewVC Image-Sound layout: image + audio per item,
   * Chinese gloss behind an info toggle, manual prev/play/next.
   */
  /**
   * Brief 177b Cluster 6 — the review-slide content layout for a modality,
   * mirroring iOS `KFEHaaiReviewVC setContentsWithReviewType:`:
   *   A,B → "image"          (imageView; HAAIReviewTypeAB)
   *   C,D → "text"           (titleLabel = titleEN; HAAIReviewTypeCD)
   *   E,F → "text-image-zh"  (titleEN + image + titleZH; HAAIReviewTypeEF)
   *   G   → "image-text"     (imageView + titleEN; HAAIReviewTypeG)
   * NOTE — this corrects the Brief 177b audit's content table, which guessed
   * "sound + text/chinese" for C/D/E/F. iOS plays audio on EVERY layout (the
   * play button + autoplay), so "sound" is universal, not a content pairing;
   * the VISUAL content is per the iOS review-type above (F shows text+image+zh
   * like E, NOT "sound+chinese").
   * @param {string} m
   * @returns {"image"|"text"|"text-image-zh"|"image-text"}
   */
  _reviewLayoutForModality(m) {
    switch (m) {
      case "C": case "D": return "text";
      case "E": case "F": return "text-image-zh";
      case "G": return "image-text";
      default:  return "image";   // A, B (and any fallback)
    }
  }

  _renderReview() {
    if (this._reviewDone) return this._renderReviewSummary();

    const total = this._testedTargets.length;
    const mmu = this._testedTargets[this._reviewIndex] ?? null;
    const modality = this._testedModalities[this._reviewIndex]
      ?? this._modalityList[0] ?? "A";
    const layout = this._reviewLayoutForModality(modality);
    const url = mmu ? this._reviewImageUrls[mmu.mmuId] : null;

    const num = document.createElement("div");
    num.className = "review-num";
    num.textContent = `${this._reviewIndex + 1} / ${total}`;

    /** @type {HTMLElement[]} */ const contentBlocks = [];

    const makeImage = () => {
      const imageWrap = document.createElement("div");
      imageWrap.className = "review-image";
      const img = document.createElement("img");
      img.alt = mmu?.phrase ?? "";
      img.loading = "eager";
      if (url) img.src = url;
      imageWrap.appendChild(img);
      return imageWrap;
    };
    const makeWord = () => {
      const word = document.createElement("div");
      word.className = "review-word";
      word.lang = "en";
      word.textContent = mmu?.phrase ?? "";
      return word;
    };
    const makeZh = () => {
      const zh = document.createElement("div");
      zh.className = "review-gloss";
      zh.lang = "zh-Hans";
      zh.textContent = mmu?.phraseZh || mmu?.phrase || "";
      return zh;
    };

    // Build content per layout. The audio play button (controls) is universal.
    if (layout === "image") {
      contentBlocks.push(makeImage());
    } else if (layout === "text") {
      contentBlocks.push(makeWord());
    } else if (layout === "image-text") {       // G: image + the English word seen
      contentBlocks.push(makeImage(), makeWord());
    } else if (layout === "text-image-zh") {    // E/F: word + image + Chinese
      contentBlocks.push(makeWord(), makeImage(), makeZh());
    }

    // Info toggle — reveals the Chinese gloss (iOS infoBtn → infoLabel). Only
    // for layouts that don't already show the Chinese inline (i.e. not E/F).
    let info = null;
    if (layout !== "text-image-zh") {
      info = document.createElement("div");
      info.className = "review-info";
      const infoBtn = document.createElement("button");
      infoBtn.type = "button";
      infoBtn.className = "review-info-btn" + (this._reviewInfoVisible ? " active" : "");
      infoBtn.setAttribute("aria-label", t("haai.review.info"));
      infoBtn.setAttribute("aria-pressed", String(this._reviewInfoVisible));
      infoBtn.textContent = "ⓘ";
      infoBtn.addEventListener("click", () => this._reviewToggleInfo());
      info.appendChild(infoBtn);
      if (this._reviewInfoVisible) {
        const gloss = makeZh();
        gloss.classList.add("review-gloss");
        info.appendChild(gloss);
      }
    }

    const controls = document.createElement("div");
    controls.className = "review-controls";
    const prevBtn = this._reviewControlButton("◀", t("haai.review.previous"), () => this._reviewPrev());
    prevBtn.disabled = this._reviewIndex <= 0;
    const playBtn = this._reviewControlButton("▶", t("haai.review.play"), () => this._reviewPlayCurrent());
    playBtn.classList.add("play");
    const nextBtn = this._reviewControlButton("▶❘", t("haai.review.next"), () => this._reviewNext());
    controls.append(prevBtn, playBtn, nextBtn);

    const wrap = html`<div class="review"><h2>${t("haai.title")}</h2></div>`;
    const container = /** @type {HTMLElement} */ (wrap.querySelector(".review"));
    container.append(num, ...contentBlocks);
    if (info) container.append(info);
    container.append(controls);
    return wrap;
  }

  /**
   * @param {string} glyph
   * @param {string} label
   * @param {() => void} onClick
   */
  _reviewControlButton(glyph, label, onClick) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "review-ctrl";
    btn.setAttribute("aria-label", label);
    btn.textContent = glyph;
    btn.addEventListener("click", onClick);
    return btn;
  }

  /**
   * iOS KFEHaaiReviewSummaryVC — "Review Completed" with three stacked buttons:
   * Back To Review / Redo Test / Leave Test.
   */
  _renderReviewSummary() {
    const screen = document.createElement("kfe-completion-screen");
    /** @type {any} */ (screen).message = t("haai.review.completed");
    /** @type {any} */ (screen).layout = "column";
    /** @type {any} */ (screen).actions = [
      { label: t("completion.haai.backToReview"), eventName: "kfe:haai:backToReview", primary: true },
      { label: t("completion.haai.redoTest"), eventName: "kfe:haai:redoTest" },
      { label: t("completion.haai.leaveTest"), eventName: "kfe:haai:leaveTest" },
    ];
    screen.addEventListener("kfe:haai:backToReview", () => this._onBackToReview());
    screen.addEventListener("kfe:haai:redoTest", () => { this._exitReview(); this._onRedo(); });
    screen.addEventListener("kfe:haai:leaveTest", () => this._onReturnToLesson());
    return html`${screen}`;
  }
}

/**
 * Resolve a HAAI modality `lang` ("target"|"source") to the BCP-47 tag.
 *
 * KFE is a Chinese-learner-of-English product (per CLAUDE.md / iOS canon).
 * The language being learned is invariant English; the UI language toggle
 * controls chrome only, not pedagogy direction. Therefore:
 *   target = English   ("en")       always — what the learner is learning.
 *   source = Chinese   ("zh-Hans")  always — the learner's L1.
 *
 * Deliberately ignores getLanguage() — that's UI chrome, not pedagogy. Brief
 * 081 replaced the prior UI-flipping resolver, which inverted the iOS canon
 * (titleEN/titleZH are picked per-modality regardless of UI mode) and would
 * have broken every text/audio stimulus in zh-mode.
 *
 * @param {"target"|"source"} lang
 * @returns {string}
 */
export function languageTag(lang) {
  return lang === "source" ? "zh-Hans" : "en";
}

/**
 * Pick the right text field off the MMU for the given modality language.
 * Falls back to phrase when phraseZh is empty so Chinese-deficient MMUs
 * still render rather than show blanks.
 * @param {import("../data/mmu-loader.js").Mmu} mmu
 * @param {"target"|"source"} lang
 */
export function textForLang(mmu, lang) {
  return lang === "source" ? (mmu.phraseZh || mmu.phrase) : mmu.phrase;
}

/**
 * @template T
 * @param {T[]} list
 * @returns {T[]}
 */
function shuffleCopy(list) {
  const out = list.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = out[i]; out[i] = out[j]; out[j] = tmp;
  }
  return out;
}
